import { useState, useEffect, useCallback } from 'react';

export type ConsentRegion = 'turkey' | 'eu' | 'other';
export type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'customized';

export interface CookiePreferences {
  essential: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentData {
  status: ConsentStatus;
  region: ConsentRegion;
  preferences: CookiePreferences;
  timestamp: number;
  expiresAt: number;
}

const CONSENT_KEY = 'sule-dental-cookie-consent';
const CONSENT_DURATION_DAYS = 180;

// EU/EEA country codes
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'IS', 'LI', 'NO', 'CH'
];

const REGION_CACHE_KEY = 'sule-dental-region-cache';
const REGION_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

const detectRegion = async (): Promise<ConsentRegion> => {
  // Check cache first to prevent rate limiting
  try {
    const cached = sessionStorage.getItem(REGION_CACHE_KEY);
    if (cached) {
      const { region, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < REGION_CACHE_DURATION) {
        return region as ConsentRegion;
      }
    }
  } catch {
    // Cache read failed, continue with detection
  }

  // First check browser language/locale
  const browserLang = navigator.language || (navigator as any).userLanguage || '';
  const browserLangLower = browserLang.toLowerCase();
  
  // If browser is set to Turkish, likely in Turkey
  if (browserLangLower.startsWith('tr')) {
    return 'turkey';
  }
  
  // Check timezone for Turkey
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone === 'Europe/Istanbul' || timezone === 'Turkey') {
      return 'turkey';
    }
  } catch {
    // Timezone detection failed, continue
  }
  
  // Try to detect via free IP geolocation API (with caching to prevent 429)
  try {
    const response = await fetch('https://ipapi.co/json/', { 
      signal: AbortSignal.timeout(3000) 
    });
    if (response.ok) {
      const data = await response.json();
      const countryCode = data.country_code?.toUpperCase();
      
      let detectedRegion: ConsentRegion = 'other';
      if (countryCode === 'TR') {
        detectedRegion = 'turkey';
      } else if (EU_COUNTRIES.includes(countryCode)) {
        detectedRegion = 'eu';
      }
      
      // Cache the result
      try {
        sessionStorage.setItem(REGION_CACHE_KEY, JSON.stringify({
          region: detectedRegion,
          timestamp: Date.now()
        }));
      } catch {
        // Cache write failed, continue
      }
      
      return detectedRegion;
    }
    // Non-ok response (429, etc.) - fall through to browser-based detection
  } catch {
    // Geolocation failed silently, use fallback
  }
  
  // Fallback: Check if browser language suggests EU
  const euLangs = ['de', 'fr', 'it', 'es', 'nl', 'pl', 'pt', 'sv', 'da', 'fi', 'el', 'cs', 'hu', 'ro', 'bg'];
  if (euLangs.some(lang => browserLangLower.startsWith(lang))) {
    return 'eu';
  }
  
  // UK check
  if (browserLangLower === 'en-gb') {
    return 'eu';
  }
  
  return 'other';
};

export const useCookieConsent = () => {
  const [consentData, setConsentData] = useState<ConsentData | null>(null);
  const [region, setRegion] = useState<ConsentRegion>('other');
  const [isLoading, setIsLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);

  // Load consent from localStorage
  useEffect(() => {
    const loadConsent = async () => {
      try {
        const stored = localStorage.getItem(CONSENT_KEY);
        console.log('[CookieConsent] Stored consent raw:', stored);
        
        if (stored) {
          const data: ConsentData = JSON.parse(stored);
          console.log('[CookieConsent] Parsed consent data:', {
            status: data.status,
            analytics: data.preferences?.analytics,
            marketing: data.preferences?.marketing,
            expiresAt: new Date(data.expiresAt).toISOString()
          });
          
          // Check if consent has expired
          if (data.expiresAt > Date.now()) {
            console.log('[CookieConsent] Consent is valid, applying...');
            setConsentData(data);
            setRegion(data.region);
            setIsLoading(false);
            setShowBanner(false);
            return;
          } else {
            console.log('[CookieConsent] Consent has expired');
          }
        } else {
          console.log('[CookieConsent] No stored consent found');
        }
        
        // No valid consent, detect region and show banner
        const detectedRegion = await detectRegion();
        console.log('[CookieConsent] Detected region:', detectedRegion);
        setRegion(detectedRegion);
        setShowBanner(true);
      } catch (e) {
        console.error('[CookieConsent] Error loading consent:', e);
        setShowBanner(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadConsent();
  }, []);

  const saveConsent = useCallback((status: ConsentStatus, preferences: CookiePreferences) => {
    const now = Date.now();
    const data: ConsentData = {
      status,
      region,
      preferences,
      timestamp: now,
      expiresAt: now + (CONSENT_DURATION_DAYS * 24 * 60 * 60 * 1000),
    };
    
    localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
    setConsentData(data);
    setShowBanner(false);
  }, [region]);

  const acceptAll = useCallback(() => {
    saveConsent('accepted', {
      essential: true,
      analytics: true,
      marketing: true,
    });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent('rejected', {
      essential: true,
      analytics: false,
      marketing: false,
    });
  }, [saveConsent]);

  const acceptCustom = useCallback((preferences: Omit<CookiePreferences, 'essential'>) => {
    saveConsent('customized', {
      essential: true,
      ...preferences,
    });
  }, [saveConsent]);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsentData(null);
    setShowBanner(true);
  }, []);

  // Check if specific cookie type is allowed
  const isAllowed = useCallback((type: keyof CookiePreferences): boolean => {
    if (type === 'essential') return true;
    if (!consentData) return false;
    return consentData.preferences[type] === true;
  }, [consentData]);

  return {
    consentData,
    region,
    isLoading,
    showBanner,
    acceptAll,
    rejectAll,
    acceptCustom,
    resetConsent,
    isAllowed,
    setShowBanner,
  };
};
